/**
 * 路径面板（DESIGN.md §7.1 补充，参照 Photoshop；ADR-0011）。
 *
 * 跟 `LayersPanel` 同一个"数据进、回调出"的纯展示分层，不认识 store。
 * 列表 + 选中预览（预览覆盖层由调用方通过 `selectedPathId` 驱动，画在
 * `bridge.setDocOverlay` 里，本组件不管怎么画）+ 建立选区/填充路径/
 * 描边路径（用当前画笔设置，`app/path-stroke.ts`）/复制/重命名/删除。
 */
import type { JSX } from 'solid-js';
import { For, Show, createSignal } from 'solid-js';
import styles from './PathsPanel.module.css';

export interface PathRow {
  readonly id: string;
  readonly name: string;
}

export interface PathsPanelProps {
  readonly paths: readonly PathRow[];
  readonly selectedPathId?: string | null | undefined;
  readonly onSelect?: ((id: string) => void) | undefined;
  readonly onRename?: ((id: string, name: string) => void) | undefined;
  readonly onDelete?: ((id: string) => void) | undefined;
  readonly onDuplicate?: ((id: string) => void) | undefined;
  readonly onLoadSelection?: ((id: string) => void) | undefined;
  readonly onFill?: ((id: string) => void) | undefined;
  readonly onStroke?: ((id: string) => void) | undefined;
}

export function PathsPanel(props: PathsPanelProps): JSX.Element {
  const [renaming, setRenaming] = createSignal<{ id: string; value: string } | null>(null);
  const selected = (): PathRow | undefined => props.paths.find((p) => p.id === props.selectedPathId);

  return (
    <div class={styles.panel} data-testid="paths-panel">
      <Show when={props.paths.length === 0}>
        <p class={styles.empty}>还没有存储的路径——钢笔工具「路径」模式画完之后点"存储路径"</p>
      </Show>
      <ul class={styles.list} role="listbox" aria-label="路径">
        <For each={props.paths}>
          {(path) => (
            <li
              class={styles.row}
              classList={{ [styles.active!]: path.id === props.selectedPathId }}
              role="option"
              aria-selected={path.id === props.selectedPathId}
              data-testid={`path-row-${path.id}`}
              onClick={() => props.onSelect?.(path.id)}
            >
              <Show
                when={renaming()?.id !== path.id}
                fallback={
                  <input
                    class={styles.rename}
                    value={renaming()!.value}
                    data-testid="path-rename"
                    ref={(el) => {
                      queueMicrotask(() => el.focus());
                    }}
                    onInput={(event) => {
                      setRenaming({ id: path.id, value: event.currentTarget.value });
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        const next = renaming();
                        setRenaming(null);
                        if (next !== null && next.value !== path.name && next.value !== '') {
                          props.onRename?.(next.id, next.value);
                        }
                      } else if (event.key === 'Escape') {
                        setRenaming(null);
                      }
                    }}
                    onBlur={() => setRenaming(null)}
                    onClick={(event) => event.stopPropagation()}
                  />
                }
              >
                <span
                  class={styles.name}
                  onClick={(event) => {
                    if (event.detail >= 2) {
                      event.stopPropagation();
                      setRenaming({ id: path.id, value: path.name });
                    }
                  }}
                >
                  {path.name}
                </span>
              </Show>
              <button
                type="button"
                class={styles.delete}
                aria-label="删除路径"
                title="删除路径"
                data-testid={`path-delete-${path.id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  props.onDelete?.(path.id);
                }}
              >
                ×
              </button>
            </li>
          )}
        </For>
      </ul>
      <div class={styles.toolbar}>
        <button
          type="button"
          data-testid="path-load-selection"
          disabled={selected() === undefined}
          onClick={() => {
            const id = selected()?.id;
            if (id !== undefined) props.onLoadSelection?.(id);
          }}
        >
          建立选区
        </button>
        <button
          type="button"
          data-testid="path-fill"
          disabled={selected() === undefined}
          onClick={() => {
            const id = selected()?.id;
            if (id !== undefined) props.onFill?.(id);
          }}
        >
          填充路径
        </button>
        <button
          type="button"
          data-testid="path-duplicate"
          disabled={selected() === undefined}
          onClick={() => {
            const id = selected()?.id;
            if (id !== undefined) props.onDuplicate?.(id);
          }}
        >
          复制路径
        </button>
        <button
          type="button"
          data-testid="path-stroke"
          disabled={selected() === undefined}
          onClick={() => {
            const id = selected()?.id;
            if (id !== undefined) props.onStroke?.(id);
          }}
        >
          描边路径
        </button>
      </div>
    </div>
  );
}
